import { IDockviewPanelProps } from "dockview-react";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api } from "./utils/api";
import { MdEdit, MdVisibility } from "react-icons/md";

type SaveStatus = "idle" | "saving" | "saved" | "error";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "bold", "italic", "underline", "strike", "blockquote",
  "list", "indent",
  "link", "image",
];

const REFRESH_INTERVAL = 30_000;

export function Documentation(_props: IDockviewPanelProps) {
  const isFullAccess = (localStorage.getItem("token") ?? "").startsWith("B8NyY");

  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastChangedAt = useRef<number>(0);

  useEffect(() => {
    const fetchDoc = async (silent: boolean) => {
      if (!silent) setIsLoading(true);
      try {
        const response = await api.get<{ content: string }>("/documentation");
        setContent(response.data.content ?? "");
      } catch (error) {
        console.error((error as Error)?.message ?? error);
        if (!silent) setSaveStatus("error");
      } finally {
        if (!silent) setIsLoading(false);
      }
    };

    fetchDoc(false);

    refreshTimer.current = setInterval(() => {
      if (!editing && Date.now() - lastChangedAt.current >= REFRESH_INTERVAL) {
        fetchDoc(true);
      }
    }, REFRESH_INTERVAL);

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [editing]);

  const saveDoc = async (value: string) => {
    setSaveStatus("saving");
    try {
      await api.put("/documentation", { content: value });
      setSaveStatus("saved");
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      console.error(error instanceof Error ? error.message : error);
      setSaveStatus("error");
    }
  };

  const handleChange = (value: string) => {
    setContent(value);
    lastChangedAt.current = Date.now();
    setSaveStatus("idle");
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveDoc(value), 1500);
  };

  if (isLoading) return <div className="p-4">Loading…</div>;

  const statusText: Record<SaveStatus, string> = {
    idle: "",
    saving: "Saving…",
    saved: "Saved",
    error: "Error saving",
  };

  const statusColor: Record<SaveStatus, string> = {
    idle: "text-transparent",
    saving: "text-gray-400",
    saved: "text-green-500",
    error: "text-red-500",
  };

  return (
    <div className="h-full w-full p-4 pt-2 flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-base font-semibold">Documentation</h1>
        <div className="flex items-center gap-3">
          {editing && (
            <span className={`text-sm ${statusColor[saveStatus]}`}>
              {statusText[saveStatus] || "."}
            </span>
          )}
          {isFullAccess && (
            editing ? (
              <MdVisibility
                size={20}
                className="cursor-pointer"
                title="View"
                onClick={() => setEditing(false)}
              />
            ) : (
              <MdEdit
                size={20}
                className="cursor-pointer"
                title="Edit"
                onClick={() => setEditing(true)}
              />
            )
          )}
        </div>
      </div>

      {editing ? (
        <div className="flex-1 min-h-0 mb-10">
          <ReactQuill
            className="h-full"
            theme="snow"
            value={content}
            onChange={handleChange}
            modules={modules}
            formats={formats}
          />
        </div>
      ) : (
        <iframe
          title="documentation-content"
          className="flex-1 border-none w-full"
          srcDoc={`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>
  body { margin: 0; padding: 8px 16px; font-family: sans-serif; font-size: 14px; line-height: 1.6; color: #000; }
  h1, h2, h3, h4, h5, h6 { margin: 0.75em 0 0.4em; font-weight: bold; }
  h1 { font-size: 2em; } h2 { font-size: 1.5em; } h3 { font-size: 1.17em; }
  p { margin: 0.5em 0; }
  ul, ol { padding-left: 2em; margin: 0.5em 0; }
  blockquote { border-left: 4px solid #ccc; margin: 0.5em 0; padding-left: 1em; color: #555; }
  a { color: #0066cc; }
  img { max-width: 100%; }
  pre { background: #f4f4f4; padding: 8px; overflow-x: auto; }
  strong { font-weight: bold; } em { font-style: italic; }
  .ql-align-center { text-align: center; }
  .ql-align-right { text-align: right; }
  .ql-align-justify { text-align: justify; }
  .ql-indent-1 { padding-left: 3em; } .ql-indent-2 { padding-left: 6em; }
  .ql-indent-3 { padding-left: 9em; } .ql-indent-4 { padding-left: 12em; }
</style>
</head>
<body>${content}</body>
</html>`}
        />
      )}
    </div>
  );
}
